import { useMemo } from 'react';
import { useAccountStore } from '../stores/accountStore';
import { describeMarket } from '../lib/kalshiTicker';
import { usePolymarketRelayFills } from '../hooks/usePolymarketRelayFills';
import { polyTradeLabel } from '../lib/polymarket/polyTradeLabel';
import {
  RECENT_FILL_LIMIT,
  buildRecentFillTape,
  type RecentFillTapeRow,
} from '../lib/fills/recentFillTape';
import { VenueChip } from './atoms/VenueChip';

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/** Kalshi labels come from the ticker; Poly labels come from the relay's
 *  persisted market identity with the browser cache and a hex preview behind
 *  it. `betTeam` is Kalshi-only — Poly's side chip carries no named team. */
function labelFor(row: RecentFillTapeRow): { marketLabel: string; betTeam: string | null } {
  if (row.venue === 'kalshi') {
    const { marketLabel, betTeam } = describeMarket(row.ticker, row.side);
    return { marketLabel, betTeam: betTeam ?? null };
  }
  return { marketLabel: polyTradeLabel(row.marketLabel, row.ticker), betTeam: null };
}

export function ActivityTicker() {
  const fills = useAccountStore((s) => s.activityTicker);
  const fillsSynced = useAccountStore((s) => s.fillsSynced);
  // Read-only history for the authenticated trader. No new poller: this is the
  // existing relay hook on its existing 10s cadence, and Poly rows are display
  // only — they never reach processFill, so Kalshi fill/first-fill/whale audio
  // is untouched and Polymarket emits no sound (#457).
  const poly = usePolymarketRelayFills({ limit: RECENT_FILL_LIMIT });

  // Both venues merged, deduplicated, newest-first and capped once. Since #769
  // the Kalshi slice already arrives newest-first — `ingestFillBatch` normalizes
  // it at the store, which is the only place that can fix it, because a batch
  // larger than the retention cap used to drop the newest fills before any
  // component could sort them. Sorting here remains load-bearing for the MERGE:
  // two venues interleave, and Poly rows are ordered independently.
  const tapeRows = useMemo(
    () => buildRecentFillTape(fills, poly.fills),
    [fills, poly.fills],
  );

  if (tapeRows.length === 0) {
    // "No recent fills" is a claim about the trader's activity, so only make it
    // once a source has actually answered. Before that, render nothing rather
    // than assert an empty history. Kalshi's arm is fillsSynced — a VALIDATED
    // fills read (#499) — never the connection status: 'connected' holds even
    // while every fills read fails or arrives malformed. A Kalshi outage still
    // doesn't blank the rail: retained or current Poly rows keep it populated,
    // and vice versa.
    const anySourceAuthoritative = fillsSynced || poly.status === 'ready';
    if (!anySourceAuthoritative) return null;
    return (
      <div className="text-muted-foreground px-3 py-2 text-xs">
        No recent fills
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {tapeRows.map((row) => {
        const { marketLabel, betTeam } = labelFor(row);
        return (
          <div
            key={row.key}
            className="hover:bg-muted/50 flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
          >
            <div className="flex items-center gap-1.5 overflow-hidden">
              <VenueChip venue={row.venue} />
              <span
                className={
                  row.action === 'buy'
                    ? 'shrink-0 font-medium text-green-400'
                    : 'shrink-0 font-medium text-red-400'
                }
              >
                {row.action.toUpperCase()}
              </span>
              <span
                className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                  row.side === 'yes'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }`}
              >
                {betTeam ? `(${betTeam}) ` : ''}
                {row.side.toUpperCase()}
              </span>
              <span className="truncate text-gray-300" title={row.ticker}>
                {marketLabel}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-muted-foreground">{row.count}</span>
              <span className="text-foreground">@ {row.priceCents}¢</span>
              <span className="text-muted-foreground/60">{timeAgo(row.executedAt)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
